import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Container, Skeleton, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import quotesApi from '../../../api/quotesApi';
import useCart from '../../../cart/useCart';
import ProductCard from '../products/ProductCard';
import ProductCarousel from '../products/ProductCarousel';

// Vitrina de productos de la home (en tendencia, mas vendido, ultimo en llegar): misma
// tarjeta y flujo de "agregar al carrito" que el catalogo, distinta solo en el orden
// pedido al backend (`sort`). Si no hay productos activos, la seccion no se muestra.
export default function HomeProductSection({ title, subtitle, sort, limit, viewAllHref }) {
  const { addItem, openDrawer } = useCart();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isCurrent = true;
    setLoading(true);
    setError(false);

    quotesApi
      .getCatalogProducts({ sort, limit })
      .then(({ data }) => {
        if (isCurrent) setProducts(data.data);
      })
      .catch(() => {
        if (isCurrent) setError(true);
      })
      .finally(() => {
        if (isCurrent) setLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [sort, limit]);

  const handleAddToCart = (product, quantity) => {
    addItem(product, quantity);
    openDrawer();
  };

  if (!loading && !error && products.length === 0) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-end', mb: 3 }}>
        <Stack spacing={0.5}>
          <Typography variant="h4">{title}</Typography>
          {subtitle && (
            <Typography color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {subtitle}
            </Typography>
          )}
        </Stack>
        {viewAllHref && (
          <Button component={RouterLink} to={viewAllHref} endIcon={<ArrowForwardIcon />}>
            Ver todo
          </Button>
        )}
      </Stack>

      {error && <Alert severity="error">No se pudo cargar esta sección. Intenta más tarde.</Alert>}

      {loading ? (
        <Stack direction="row" spacing={3} sx={{ overflow: 'hidden' }}>
          {Array.from({ length: 4 }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={index} sx={{ flex: '0 0 auto', width: { xs: '75%', sm: '46%', md: '23%' } }}>
              <Skeleton variant="rounded" height={220} />
            </Box>
          ))}
        </Stack>
      ) : (
        <ProductCarousel>
          {products.map((product) => (
            <ProductCard key={product.id} product={product} onAddToCart={handleAddToCart} />
          ))}
        </ProductCarousel>
      )}
    </Container>
  );
}

HomeProductSection.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  sort: PropTypes.oneOf(['random', 'best_selling', 'latest', 'name']).isRequired,
  limit: PropTypes.number,
  viewAllHref: PropTypes.string,
};

HomeProductSection.defaultProps = {
  subtitle: '',
  limit: 8,
  viewAllHref: '',
};
